// Types
import ComparisonItem from '@/types/comparison-item';
import ShelvableInstrument from '@/types/shelvable-instrument';
import User from '@/types/user';
import UserShelf from '@/types/user-shelf';

// Utils
import { formatLocalizedString } from '@/utils/i18n';
import { COMPARISON_ITEM_TYPES } from './constants';

/**
 * Helper function to find the first matching shelf name from a collection of shelves
 * @param shelves - Array of shelves IDs
 * @param user - The user object containing the shelves data
 * @returns The formatted localized string name of the shelf, or undefined if not found
 */
const findShelfName = (shelves: string[], user: User): string | undefined => {
  if (!user?.shelves || !Array.isArray(user.shelves)) {
    return undefined;
  }

  for (const shelf of shelves) {
    const userShelf = user.shelves.find((userShelf: UserShelf) => userShelf.id === shelf);

    if (userShelf?.shelfName) {
      return formatLocalizedString(userShelf.shelfName);
    }
  }

  return undefined;
};

/**
 * Maps a shelf ID to its localized name from the user's shelves data
 * @param shelfId - The ID of the shelf to find
 * @param user - The user object containing the shelves data
 * @returns The formatted localized string name of the shelf, or undefined if not found
 */
export const getShelfNameById = (shelfId: string, user?: User | null): string | undefined => {
  if (!user?.shelves || !Array.isArray(user.shelves)) {
    return undefined;
  }

  const shelf = user.shelves.find((shelf: UserShelf) => shelf.id === shelfId);
  if (!shelf?.shelfName) {
    return undefined;
  }

  return formatLocalizedString(shelf.shelfName);
};

/**
 * Gets the shelf name from an instrument by finding matching shelf ID
 * between the instrument's shelves and the user's shelves
 * @param instrumentShelves - An instrument containing filteredShelves property
 * @param user - The user object containing the shelves data
 * @returns The formatted localized string name of the shelf, or undefined if not found
 */
export const getShelfNameFromShelvableInstrument = (instrument: ShelvableInstrument, user: User): string | undefined => {
  if (!instrument.filteredShelves || instrument.filteredShelves.length === 0) {
    return undefined;
  }

  // In this case the filteredShelves are always strings
  const shelves: string[] = instrument.filteredShelves as string[];
  return findShelfName(shelves, user);
};

/**
 * Gets the shelf name from a comparison item by checking the first holding's filteredShelves
 * Works for standalone instrument (funds, stocks, alts, etc)
 * @param item - The comparison item (fund, stock, alt, etc) containing the holding
 * @param user - The user object containing the shelves data
 * @returns The formatted localized string name of the shelf, or undefined if not found
 */
export const getShelfNameFromComparisonItem = (item: ComparisonItem, user: User): string | undefined => {
  if (!item.holdings || item.holdings.length === 0) {
    return undefined;
  }

  if ([COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.GROUP].includes(item.type as COMPARISON_ITEM_TYPES)) {
    return undefined;
  }

  const instrument = item.holdings[0];

  if (!instrument.filteredShelves || instrument.filteredShelves.length === 0) {
    return undefined;
  }

  // In this case the filteredShelves are always strings
  const shelves: string[] = instrument.filteredShelves as string[];
  return findShelfName(shelves, user);
};
